import { Link } from "react-router-dom";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  {
    id: "overview",
    heading: "Purpose of this Disclosure",
    body: (
      <>
        <p>
          This Risk Disclosure explains the principal risks of buying, selling, holding and
          transferring virtual digital assets (&ldquo;<strong>VDAs</strong>&rdquo;) through PaisaCX,
          including our P2P marketplace and OTC desk. It forms part of, and should be read with, our{" "}
          <Link to="/terms">Terms of Service</Link>.
        </p>
        <p>
          This document does not list every possible risk. You should only trade if you understand
          how VDAs work and can afford to lose the entire amount you commit.
        </p>
      </>
    ),
  },
  {
    id: "market",
    heading: "Market & Volatility Risk",
    body: (
      <>
        <p>
          <strong>VDA prices are highly volatile and can move sharply within minutes.</strong> Prices
          are driven by supply and demand across global venues, news, sentiment, regulatory action and
          large holders, and can fall to zero.
        </p>
        <ul>
          <li>past performance is not a reliable indicator of future results;</li>
          <li>stablecoins such as USDT aim to track a reference currency but can lose their peg;</li>
          <li>prices quoted on PaisaCX may differ from prices on other platforms;</li>
          <li>you may be unable to sell at your desired price during fast or thin markets.</li>
        </ul>
      </>
    ),
  },
  {
    id: "liquidity",
    heading: "Liquidity Risk",
    body: (
      <p>
        The depth of the P2P marketplace and OTC liquidity depends on other participants and our
        liquidity partners. At times there may be few or no counterparties at an acceptable price,
        quotes may be withdrawn, and large orders may only be filled at a less favourable price or
        not at all.
      </p>
    ),
  },
  {
    id: "p2p",
    heading: "P2P Counterparty & Payment Risk",
    body: (
      <>
        <p>
          In P2P trades the fiat payment moves directly between you and the other user through
          banking or UPI rails. PaisaCX holds the seller&rsquo;s crypto in escrow but does not control
          the fiat leg. You should be aware that:
        </p>
        <ul>
          <li>a counterparty may fail to pay, pay late, or pay from a third-party account;</li>
          <li>payments you receive could later be reported as fraudulent, leading to bank account
              freezes or chargeback claims by law-enforcement or banks;</li>
          <li>releasing crypto before confirming funds have actually arrived in your own account can
              result in permanent loss;</li>
          <li>disputes are resolved on the evidence available and outcomes are not guaranteed.</li>
        </ul>
        <p>
          Only release escrow after checking your bank statement, never communicate or settle outside
          the PaisaCX order chat, and never accept payments from names that differ from the
          counterparty&rsquo;s verified name.
        </p>
      </>
    ),
  },
  {
    id: "otc",
    heading: "OTC Quote & Settlement Risk",
    body: (
      <p>
        OTC quotes are valid only for the window shown and include a spread over the reference
        price. Prices can move between quote and settlement, and quotes may be re-priced or
        cancelled if funds are not received in time. Settlement depends on banking hours, partner
        bank processing and on-chain confirmations, any of which can delay completion.
      </p>
    ),
  },
  {
    id: "technology",
    heading: "Technology & Blockchain Risk",
    body: (
      <>
        <ul>
          <li><strong>Irreversibility:</strong> on-chain transfers cannot be reversed once confirmed.
              Sending to a wrong address or wrong network can cause permanent loss.</li>
          <li><strong>Network risk:</strong> blockchains can suffer congestion, high fees, forks,
              reorganisations or protocol bugs.</li>
          <li><strong>Platform risk:</strong> the platform may be unavailable during maintenance,
              outages, cyber-attacks or third-party failures, and you may be unable to trade or
              withdraw during that time.</li>
          <li><strong>Smart-contract and token risk:</strong> token issuers can change, freeze or
              blacklist tokens, independently of PaisaCX.</li>
        </ul>
      </>
    ),
  },
  {
    id: "security",
    heading: "Account Security Risk",
    body: (
      <p>
        You are responsible for securing your login credentials, email, mobile number and devices.
        Phishing, SIM-swap, malware and social-engineering attacks are common. PaisaCX staff will
        never ask for your password, OTP or 2FA code. Enable two-factor authentication and treat any
        unexpected request for codes or remote access as fraud.
      </p>
    ),
  },
  {
    id: "regulatory",
    heading: "Regulatory & Legal Risk",
    body: (
      <>
        <p>
          VDAs are not legal tender in India and are not backed by the Government of India or the
          Reserve Bank of India. They are not covered by deposit insurance or investor-protection
          schemes, and there may be no regulatory recourse for losses.
        </p>
        <p>
          Laws, regulations and banking policies relating to VDAs continue to evolve. Future changes
          could restrict or prohibit certain activities, affect the availability of INR rails, or
          require us to suspend services, delist assets or close accounts.
        </p>
      </>
    ),
  },
  {
    id: "tax",
    heading: "Tax Risk",
    body: (
      <p>
        Income from the transfer of VDAs is taxed in India under the Income-tax Act, 1961, and TDS
        under Section 194S may be deducted on qualifying transactions. Losses may not be set off in
        the way they are for other assets. Tax rules can change. You are responsible for your own tax
        reporting; the TDS applied on PaisaCX is shown on our <Link to="/fees">fee schedule</Link>.
        Consult a qualified tax adviser.
      </p>
    ),
  },
  {
    id: "no-advice",
    heading: "No Investment Advice",
    body: (
      <p>
        Market data, articles, blog posts and any other content on PaisaCX are for information only
        and are not investment, legal or tax advice, or a recommendation to buy or sell any asset.
        Every trading decision is yours alone.
      </p>
    ),
  },
  {
    id: "acknowledgement",
    heading: "Your Acknowledgement",
    body: (
      <p>
        By using PaisaCX you confirm that you have read and understood this Risk Disclosure, that you
        are trading with your own funds, and that you accept the risks described above. If you have
        questions, please <Link to="/contact">contact our support team</Link> before trading.
      </p>
    ),
  },
];

export default function RiskDisclosure() {
  const intro = (
    <>
      Crypto prices can swing sharply and you can lose <strong>all of the money you put in</strong>.
      Crypto is not legal tender in India and there is no deposit insurance. In P2P trades, always
      confirm the money has reached your own bank account before releasing escrow. On-chain transfers
      cannot be undone, and tax rules apply to your gains. Only trade what you can afford to lose.
    </>
  );
  return (
    <LegalLayout
      testid="risk-disclosure-page"
      title="Risk Disclosure"
      description="The key risks of trading and holding virtual digital assets on PaisaCX. Please read this carefully before you trade."
      lastUpdated="6 October 2026"
      intro={intro}
      sections={sections}
    />
  );
}
